/**
 * Drawing UI state — one store per open board view, owned by BoardScreen (the
 * top-bar Draw toggle and the canvas's DrawingLayer/DrawToolbar all read it).
 * Same pattern as the comments `CommentsUiStore`: LOCAL view state only; the
 * strokes themselves are `drawing` elements in the Yjs doc.
 *
 * `mode` (draw tool active), the current `color`, and `thickness` persist for
 * the session so a user keeps their pen between strokes.
 */
import { createStore, type StoreApi } from "zustand/vanilla";

/** Pen thickness presets (board px) offered by the toolbar. */
export const THICKNESS_PRESETS = [2, 4, 8, 16] as const;
export const MIN_THICKNESS = 1;
export const MAX_THICKNESS = 64;

/**
 * Pen color presets — saturated ink colors (a drawing sits ON the board, so it
 * wants a strong stroke, unlike the pastel cell-fill presets). Lowercase
 * `#rrggbb`, the same normal form `normalizeHexColor` produces, so preset vs
 * custom equality is plain string comparison.
 */
export const PEN_PRESETS: readonly string[] = [
  "#e11d48", // red
  "#ea580c", // orange
  "#ca8a04", // amber
  "#16a34a", // green
  "#0891b2", // cyan
  "#2563eb", // blue
  "#7c3aed", // violet
  "#111827", // near-black
];

/** Default pen — a readable mid-weight stroke in a strong red. */
export const DEFAULT_DRAW_COLOR = "#e11d48";
export const DEFAULT_DRAW_THICKNESS = 4;

export interface DrawUiState {
  /** Draw tool active: the cursor becomes a crosshair and drags paint strokes. */
  mode: boolean;
  /** Current stroke color (validated hex). */
  color: string;
  /** Current stroke thickness in board px. */
  thickness: number;

  setMode(mode: boolean): void;
  setColor(color: string): void;
  setThickness(thickness: number): void;
}

export type DrawUiStore = StoreApi<DrawUiState>;

export function createDrawUiStore(): DrawUiStore {
  return createStore<DrawUiState>((set) => ({
    mode: false,
    color: DEFAULT_DRAW_COLOR,
    thickness: DEFAULT_DRAW_THICKNESS,
    setMode: (mode) => set({ mode }),
    setColor: (color) => set({ color }),
    setThickness: (thickness) =>
      set({ thickness: Math.min(MAX_THICKNESS, Math.max(MIN_THICKNESS, thickness)) }),
  }));
}
